import { fetchAllPages, cacheSuccessfulRequest } from './pagination';
import axios from 'axios';

// Base API configuration
// Use relative URL for production (same domain as frontend)
// Use localhost for development
const API_BASE_URL = import.meta.env.PROD 
  ? '/api' 
  : (import.meta.env.VITE_API_URL || 'http://localhost:3000/api');

// Mobile-specific API URL detection
const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

// For mobile devices in production, ensure we use the full URL if needed
const getApiBaseUrl = () => {
  if (import.meta.env.PROD) {
    // For iOS devices, sometimes relative URLs fail - try absolute URL fallback
    if (isIOS && window.location.origin) {
      return window.location.origin + '/api';
    }
    // In production, use relative URL for same-origin requests
    return '/api';
  } else {
    // In development, use the configured URL
    return import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  }
};

const FINAL_API_BASE_URL = getApiBaseUrl();

// Create axios instance with iOS Safari-specific configurations
const api = axios.create({
  baseURL: FINAL_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // iOS Safari-specific configurations
  timeout: isIOS ? 30000 : 10000, // Longer timeout for iOS devices
  withCredentials: false, // Explicit setting for iOS Safari
  // Add cache control for iOS Safari
  ...(isIOS && {
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    }
  })
});

// Token management
const TOKEN_KEY = 'readtheword_token';

export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

export const setToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem(TOKEN_KEY);
    delete api.defaults.headers.common['Authorization'];
  }
};

// Set token from localStorage on app start
const existingToken = getToken();
if (existingToken) {
  setToken(existingToken);
}

// Response interceptor for handling token expiration and rate limiting
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      setToken(null);
      // Don't redirect here - let the app handle it through context
      // window.location.href = '/auth';
    } else if (error.response?.status === 429) {
      // Rate limit exceeded - add retry logic
      console.warn('Rate limit exceeded, will retry after delay');
      // For now, just log the warning. In a production app, you might want to implement
      // exponential backoff retry logic here
    } else if (!error.response && error.code === 'NETWORK_ERROR') {
      // Network error - log for debugging
      console.error('Network error detected:', {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        isMobile: isMobile,
        isIOS: isIOS,
        userAgent: navigator.userAgent,
        connectionType: navigator.connection?.effectiveType,
        onLine: navigator.onLine
      });
    } else if (error.code === 'ECONNABORTED' && isIOS) {
      // iOS Safari timeout - common issue
      console.error('iOS Safari timeout detected:', {
        url: error.config?.url,
        timeout: error.config?.timeout,
        userAgent: navigator.userAgent
      });
    }
    return Promise.reject(error);
  }
);

// Auth API methods
export const authApi = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => {
    setToken(null);
    return Promise.resolve();
  },
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
};

// User API methods
export const userApi = {
  updateProfile: (userData) => api.put('/users/profile', userData),
  changePassword: (passwordData) => api.put('/users/password', passwordData),
  deleteAccount: () => api.delete('/users/account'),
  
  // User Preferences
  getPreferences: () => api.get('/users/preferences'),
  updatePreferences: (preferences) => api.put('/users/preferences', preferences),
  
  // Onboarding
  getOnboardingStatus: () => api.get('/users/onboarding'),
  advanceOnboarding: (skip = false) => api.post('/users/onboarding/advance', { skip }),
  completeOnboarding: () => api.post('/users/onboarding/complete'),
  resetOnboarding: () => api.post('/users/onboarding/reset'),
};

// Family API methods
export const familyApi = {
  getFamilies: () => api.get('/families'),
  getFamily: (id) => api.get(`/families/${id}`),
  createFamily: (familyData) => api.post('/families', familyData),
  joinFamily: (inviteCode) => api.post('/families/join', { inviteCode }),
  leaveFamily: (id) => api.delete(`/families/${id}/leave`),
  deleteFamily: (id) => api.delete(`/families/${id}`),
  addReaderToFamily: (familyId, readerId) => api.post(`/families/${familyId}/readers/${readerId}`),
  removeReaderFromFamily: (familyId, readerId) => api.delete(`/families/${familyId}/readers/${readerId}`),
};

// Reader API methods
export const readerApi = {
  getReaders: async () => {
    try {
      return await api.get('/readers');
    } catch (error) {
      // For iOS devices, try fetch fallback if axios fails
      if (isIOS && (error.code === 'NETWORK_ERROR' || error.code === 'ECONNABORTED' || !error.response)) {
        console.log('🍎 iOS Fallback - Trying fetch for readers');
        try {
          return await iosFallbackRequest('/readers');
        } catch (fetchError) {
          console.error('🍎 iOS Fallback - Fetch also failed for readers:', fetchError);
          throw error; // Throw original axios error
        }
      }
      throw error;
    }
  },
  createReader: (readerData) => api.post('/readers', readerData),
  updateReader: (id, readerData) => api.put(`/readers/${id}`, readerData),
  deleteReader: (id) => api.delete(`/readers/${id}`),
  getReaderProgress: (id) => api.get(`/readers/${id}/progress`),
  // WS2: the chapter after this reader's most recent report, for one-tap logging
  getNextChapter: (id) => api.get(`/readers/${id}/next-chapter`),
};

// Chapters API methods
export const chapterApi = {
  getAllChapters: cacheSuccessfulRequest(() => fetchAllPages(params => chapterApi.getChapters(params), 'chapters')),
  getChapters: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/chapters${queryString ? `?${queryString}` : ''}`);
  },
  getChapter: (id) => api.get(`/chapters/${id}`),
  getBooks: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/chapters/books${queryString ? `?${queryString}` : ''}`);
  },
  getBookChapters: (bookName) => api.get(`/chapters/book/${encodeURIComponent(bookName)}`),
  searchChapters: (query) => api.get(`/chapters/search/${encodeURIComponent(query)}`),
};

// Reports API methods
export const reportApi = {
  getAllRawReports: () => fetchAllPages(params => api.get('/raw-chapter-reports', { params }), 'reports'),
  getAllReports: (params = {}) => fetchAllPages(pageParams => reportApi.getReports(pageParams), 'reports', params),
  // Start a multi-part reporting session
  startSession: (sessionId, expectedOperations) =>
    api.post('/reports/start-session', { sessionId, expectedOperations }),
  
  // End a multi-part reporting session  
  endSession: (sessionId) =>
    api.post('/reports/end-session', { sessionId }),
  getReports: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/reports${queryString ? `?${queryString}` : ''}`);
  },
  getStats: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/reports/stats${queryString ? `?${queryString}` : ''}`);
  },
  getGlobalStats: () => api.get('/reports/global-stats'),
  getBookProgress: () => api.get('/reports/book-progress'),
  getNextCompletionChapters: () => api.get('/reports/next-completion-chapters'),
  getLastBatch: () => api.get('/reports/last-batch'),
  undoLastBatch: () => api.delete('/reports/undo-last-batch'),
  createReport: (reportData) => api.post('/reports', reportData),
  createFamilyReport: (familyId, reportData) => api.post(`/reports/family/${familyId}`, reportData),
  updateReport: (id, reportData) => api.put(`/reports/${id}`, reportData),
  deleteReport: (id) => api.delete(`/reports/${id}`),
};

// Challenge API methods (2.0 WS4)
export const challengeApi = {
  getActive: () => api.get('/challenges/active'),
  getChallenges: () => api.get('/challenges'),
  getStats: (id, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/challenges/${id}/stats${queryString ? `?${queryString}` : ''}`);
  },
  getMyChurchProgress: () => api.get('/challenges/active/my-church'),
  // WS5: three still-needed chapters this reader could claim
  getSuggestions: (id, readerId) => api.get(`/challenges/${id}/suggest`, { params: { readerId } }),

  // Admin
  getAllForAdmin: () => api.get('/challenges/admin/all'),
  createChallenge: (data) => api.post('/challenges/admin', data),
  updateChallenge: (id, data) => api.put(`/challenges/admin/${id}`, data),
  deleteChallenge: (id) => api.delete(`/challenges/admin/${id}`),
  saveGoal: (challengeId, goal) => api.post(`/challenges/admin/${challengeId}/goals`, goal),
  deleteGoal: (goalId) => api.delete(`/challenges/admin/goals/${goalId}`),
};

// Chapter claims (2.0 WS5)
export const claimApi = {
  create: (data) => api.post('/claims', data),
  release: (id) => api.delete(`/claims/${id}`),
};

// ESV API methods
export const esvApi = {
  getPassageText: (passage, options = {}) => {
    const queryString = new URLSearchParams({ passage, ...options }).toString();
    return api.get(`/esv/passage/text?${queryString}`);
  },
  getPassageHTML: (passage, options = {}) => {
    const queryString = new URLSearchParams({ passage, ...options }).toString();
    return api.get(`/esv/passage/html?${queryString}`);
  },
  getPassageAudio: (passage) => {
    const queryString = new URLSearchParams({ passage }).toString();
    return api.get(`/esv/passage/audio?${queryString}`);
  },
  getChapterText: (chapterId, options = {}) => {
    const queryString = new URLSearchParams(options).toString();
    return api.get(`/esv/chapter/${chapterId}/text${queryString ? `?${queryString}` : ''}`);
  },
  getChapterHTML: (chapterId, options = {}) => {
    const queryString = new URLSearchParams(options).toString();
    return api.get(`/esv/chapter/${chapterId}/html${queryString ? `?${queryString}` : ''}`);
  },
  getChapterAudio: (chapterId) => {
    return api.get(`/esv/chapter/${chapterId}/audio`);
  },
  searchPassages: (query, options = {}) => {
    const queryString = new URLSearchParams({ q: query, ...options }).toString();
    return api.get(`/esv/search?${queryString}`);
  },
  getBatchPassages: (passages, format = 'text', options = {}) => {
    return api.post('/esv/passages/batch', { passages, format, options });
  },
};

// Church API methods
export const churchApi = {
  getChurches: () => api.get('/churches'),
  getChurch: (id) => api.get(`/churches/${id}`),
  createChurch: (churchData) => api.post('/churches', churchData),
  updateChurch: (id, churchData) => api.put(`/churches/${id}`, churchData),
  searchChurches: (query) => api.get(`/churches/search/${encodeURIComponent(query)}`),
  joinChurch: (churchId) => api.post('/churches/join', { churchId }),
  joinByCode: (joinCode) => api.post('/churches/join', { joinCode }),
  leaveChurch: () => api.post('/churches/leave'),
  getCurrentChurch: () => api.get('/churches/current'),
  getAdministeredChurches: () => api.get('/churches/administered'),
  
  // Pending members management
  getPendingMembers: (churchId) => api.get(`/churches/${churchId}/pending-members`),
  approveMember: (churchId, userId) => api.post(`/churches/${churchId}/members/${userId}/status`, { status: 'approved' }),
  rejectMember: (churchId, userId) => api.post(`/churches/${churchId}/members/${userId}/status`, { status: 'rejected' }),
  
  // Admin assignment
  getAvailableAdmins: (churchId) => api.get(`/churches/${churchId}/available-admins`),
  assignAdmin: (churchId, userId) => api.post(`/churches/${churchId}/assign-admin`, { userId }),
};

// iOS Safari fallback function using fetch API
const iosFallbackRequest = async (url, options = {}) => {
  const fullUrl = FINAL_API_BASE_URL + url;
  const token = getToken();
  
  console.log('🍎 iOS Fallback - Making fetch request to:', fullUrl);
  
  const response = await fetch(fullUrl, {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
      ...options.headers
    },
    ...(options.body && { body: JSON.stringify(options.body) })
  });
  
  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
    error.response = { status: response.status, statusText: response.statusText };
    throw error;
  }
  
  const data = await response.json();
  return { data };
};

// Gamification API methods
export const gamificationApi = {
  getReaderProfile: async (readerId) => {
    console.log('🔍 Gamification API - Starting getReaderProfile for:', readerId);
    try {
      console.log('🔍 Gamification API - Making axios request to:', `/gamification/profile/${readerId}`);
      const response = await api.get(`/gamification/profile/${readerId}`);
      console.log('✅ Gamification API - Reader profile loaded successfully');
      return response;
    } catch (error) {
      console.error('❌ Gamification API - Reader profile failed:', error);
      console.error('❌ Gamification API - Error details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        timeout: error.config?.timeout
      });
      
      // For iOS devices, try fetch fallback if axios fails
      if (isIOS && (error.code === 'NETWORK_ERROR' || error.code === 'ECONNABORTED' || !error.response)) {
        console.log('🍎 iOS Fallback - Trying fetch for reader profile');
        try {
          const fallbackResponse = await iosFallbackRequest(`/gamification/profile/${readerId}`);
          console.log('✅ iOS Fallback - Reader profile loaded via fetch');
          return fallbackResponse;
        } catch (fetchError) {
          console.error('🍎 iOS Fallback - Fetch also failed:', fetchError);
          throw error; // Throw original axios error
        }
      }
      throw error;
    }
  },
  getLeaderboard: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/gamification/leaderboard${queryString ? `?${queryString}` : ''}`);
  },
  getBadges: () => api.get('/gamification/badges'),
  getReaderBadges: (readerId) => api.get(`/gamification/badges/${readerId}`),
  getLevels: () => api.get('/gamification/levels'),
  getTransactions: (readerId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/gamification/transactions/${readerId}${queryString ? `?${queryString}` : ''}`);
  },
  getCompletion: (readerId) => api.get(`/gamification/completion/${readerId}`),
  
  // Admin methods
  createLevel: (levelData) => api.post('/gamification/admin/levels', levelData),
  createBadge: (badgeData) => api.post('/gamification/admin/badges', badgeData),
  awardPoints: (pointsData) => api.post('/gamification/admin/award-points', pointsData),
  getGamificationStats: () => api.get('/gamification/admin/stats'),
};

// Reading Plans API methods
export const readingPlansApi = {
  // Get all available reading plans
  getPlans: () => api.get('/plans'),
  
  // Get specific plan details
  getPlan: (planId) => api.get(`/plans/${planId}`),
  
  // Get reader's plan progress
  getReaderProgress: (readerId) => api.get(`/plans/reader/${readerId}/progress`),
  
  // Start a reading plan for a reader
  startPlan: (readerId, planId, options = {}) => api.post(`/plans/reader/${readerId}/start/${planId}`, options),
  
  // Get today's recommended chapters
  getTodaysChapters: (readerId, planId, date) => {
    const params = date ? { date } : {};
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/plans/reader/${readerId}/plan/${planId}/today${queryString ? `?${queryString}` : ''}`);
  },
  
  // Get upcoming schedule
  getUpcomingSchedule: (readerId, planId, days = 7) => {
    return api.get(`/plans/reader/${readerId}/plan/${planId}/schedule?days=${days}`);
  },
  
  // Get unread chapters for a plan (legacy)
  getUnreadChapters: (readerId, planId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/plans/reader/${readerId}/plan/${planId}/unread${queryString ? `?${queryString}` : ''}`);
  },
  
  // Update plan progress
  updateProgress: (readerId, planId, progressData) => api.put(`/plans/reader/${readerId}/plan/${planId}/progress`, progressData),
};

// Notification API methods
export const notificationApi = {
  // User endpoints
  getUserNotifications: (params = {}) => {
    // Filter out empty values to avoid 400 errors
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, value]) => 
        value !== null && value !== undefined && value !== ''
      )
    );
    const queryString = new URLSearchParams(cleanParams).toString();
    return api.get(`/notifications${queryString ? `?${queryString}` : ''}`);
  },
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (notificationId, action = null) => {
    const data = action ? { action } : {};
    return api.put(`/notifications/${notificationId}/read`, data);
  },
  markAllAsRead: () => api.put('/notifications/read-all'),
  registerPushToken: (tokenData) => api.post('/notifications/push-token', tokenData),
  unregisterPushToken: (token) => api.delete('/notifications/push-token', { data: { token } }),
  
  // Admin endpoints
  createNotification: (notificationData) => api.post('/notifications/admin', notificationData),
  getAllNotifications: (params = {}) => {
    // Filter out empty values to avoid 400 errors
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, value]) => 
        value !== null && value !== undefined && value !== ''
      )
    );
    const queryString = new URLSearchParams(cleanParams).toString();
    return api.get(`/notifications/admin${queryString ? `?${queryString}` : ''}`);
  },
  getNotificationDetails: (notificationId) => api.get(`/notifications/admin/${notificationId}`),
  getNotificationStats: (notificationId) => api.get(`/notifications/admin/${notificationId}/stats`),
  updateNotification: (notificationId, updateData) => api.put(`/notifications/admin/${notificationId}`, updateData),
  deleteNotification: (notificationId) => api.delete(`/notifications/admin/${notificationId}`),
  sendScheduledNotification: (notificationId) => api.post(`/notifications/admin/${notificationId}/send`),
};

export default api;
